import { TagDetailsFormView } from '../view/TagDetailsFormView';
import { templateTagDetailsForm } from '../view/template';
import { TagDetailsFormRepository } from '../repository/TagDetailsFormRepository';
import { BaseWebComponentController } from '../../../../framework/BaseWebComponentController';
import { TagDetailsFormWebComponent } from '..';
import { TagDetailsFormViewData } from '../types/TagDetailsFormViewData';
import { Dependencies } from '../../../types/dependencies';
import { TagTypes } from '../../../types/tag-types';
import { Company } from '../../../types/tag-data';

export class TagDetailsFormController extends BaseWebComponentController {
  public component: TagDetailsFormWebComponent;
  public dataRepository: TagDetailsFormRepository;
  public view: TagDetailsFormView;
  public viewConstructor: typeof TagDetailsFormView;
  public dataRepositoryConstructor: typeof TagDetailsFormRepository;
  public dependencies: Dependencies;

  private company: Company;

  constructor(component: TagDetailsFormWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: TagDetailsFormView,
      viewTemplate: templateTagDetailsForm,
      dataRepositoryConstructor: TagDetailsFormRepository,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$tagTypeFormField,
        event: 'radioSelectionChanged',
        callback: this.onTagTypeRadioSelectionChanged,
      },
      {
        element: this.view.dom.$endpointNameEnabledFormField.Input,
        event: 'change',
        callback: this.onEndpointNameEnabledChanged,
      },
      {
        element: this.view.dom.$ipEnabledFormField.Input,
        event: 'change',
        callback: this.onIpEnabledChanged,
      },
      {
        element: this.view.dom.$osEnabledFormField.Input,
        event: 'change',
        callback: this.onOsEnabledChanged,
      },
      {
        element: this.view.dom.$endpointTypeEnabledFormField.Input,
        event: 'change',
        callback: this.onEndpointTypeEnabledChanged,
      },
      {
        element: this.view.dom.$tagCompanyFormField.Input,
        event: 'selectionChange',
        callback: this.onTagCompanyDropdownChanged,
      },
    ];
  }

  onTagTypeRadioSelectionChanged(ev) {
    switch (ev.detail.selectedValue) {
      case TagTypes.TAG_TYPE_CUSTOM:
        this.view.dom.$ruleDetailsContainer.setAttribute('disabled', '');
        this.view.formGroup.removeFormChild(['rules']);
        this.view.tagRulesFormGroup.setDisabled(true);
        break;
      case TagTypes.TAG_TYPE_AUTOMATIC:
        this.view.dom.$ruleDetailsContainer.removeAttribute('disabled');
        this.view.tagRulesFormGroup.setDisabled(false);
        this.view.formGroup.addFormChild(['rules'], this.view.tagRulesFormGroup);
        break;
    }
    return;
  }

  private onEndpointNameEnabledChanged() {
    this.onRuleEnabledChanged(
      this.view.dom.$endpointNameEnabledFormField,
      this.view.dom.$endpointNameRuleFormField,
      'endpointName'
    );
  }

  private onIpEnabledChanged() {
    this.onRuleEnabledChanged(this.view.dom.$ipEnabledFormField, this.view.dom.$ipRuleFormField, 'ip');
  }

  private onOsEnabledChanged() {
    this.onRuleEnabledChanged(this.view.dom.$osEnabledFormField, this.view.dom.$osRuleFormField, 'os');
  }

  private onEndpointTypeEnabledChanged() {
    this.onRuleEnabledChanged(
      this.view.dom.$endpointTypeEnabledFormField,
      this.view.dom.$endpointTypeRuleFormField,
      'endpointType'
    );
  }

  private onTagCompanyDropdownChanged() {
    this.view.formGroup?.validate();
  }

  private onRuleEnabledChanged($enabledFormField, $ruleFormField, $stringName: string) {
    const enabled = $enabledFormField.value;
    $ruleFormField.disabled = !enabled;

    if (enabled) {
      this.view.tagRulesFormGroup.addFormChild([$stringName, 'rule'], $ruleFormField);
    } else {
      this.view.tagRulesFormGroup.removeFormChild([$stringName, 'rule']);
    }

    this.triggerRulesFormValidation();
  }

  private triggerRulesFormValidation() {
    this.view.tagRulesFormGroup.validate();
  }

  public setCompany(value: Company) {
    this.company = value;
  }

  async getDataForView(): Promise<TagDetailsFormViewData> {
    const osTypes = this.dependencies.getOperatingSystemTypes();
    const endpointTypes = this.dependencies.getEndpointTypes();

    return {
      osTypes: osTypes,
      endpointTypes: endpointTypes,
      formContext: this.component.formContext,
      company: this.company,
    };
  }
}
